// "Places" are rooms/areas on campus with extra, type-specific information
// (study spots, libraries, restaurants, bathrooms, clubs, offices) — a flat
// array. The Welcome Day viewer surfaces bathrooms from here. Ported from
// UMapsTools; load/save now talk to the CRA/Express/Vercel API (KV-backed).

import { API_BASE, authHeaders } from './api.js';

// The six starting place types, each with its display label and default icon.
export const PLACE_TYPES = {
  study:      { label: 'Study Spot', icon: '📚', color: '#3949ab' },
  library:    { label: 'Library',    icon: '🏛', color: '#6d4c41' },
  restaurant: { label: 'Restaurant', icon: '🍽', color: '#e64a19' },
  bathroom:   { label: 'Bathroom',   icon: '🚻', color: '#00838f' },
  club:       { label: 'Club',       icon: '🎉', color: '#8e24aa' },
  office:     { label: 'Office',     icon: '🏢', color: '#455a64' },
};
export const PLACE_TYPE_KEYS = Object.keys(PLACE_TYPES);

export const STUDY_AMENITIES = [
  { key: 'plugs',    label: '🔌 Plugs' },
  { key: 'cushions', label: '🛋 Cushions' },
  { key: 'quiet',    label: '🤫 Quiet zone' },
  { key: 'loud',     label: '🗣 Loud zone' },
  { key: 'noTables', label: '🚫 No tables' },
];

export const BATHROOM_TYPES = [
  { key: 'mens',      label: "Men's" },
  { key: 'womens',    label: "Women's" },
  { key: 'universal', label: 'Universal' },
];

export const CAPACITY_LEVELS = [
  { key: 'low',    label: 'Low' },
  { key: 'medium', label: 'Medium' },
  { key: 'high',   label: 'High' },
];

let idSeq = 0;
export function newPlaceId() {
  return `place_${Date.now().toString(36)}_${(idSeq++).toString(36)}`;
}

/** A fresh place of the given type with all fields present (blank). */
export function makePlace(type, at = null) {
  const base = {
    id: newPlaceId(), type, name: '', icon: PLACE_TYPES[type]?.icon || '📍',
    lat: at?.lat ?? null, lng: at?.lng ?? null, building: '', roomNumber: '', hours: '', images: [],
  };
  switch (type) {
    case 'study':
      return { ...base, distance: '', capacity: '', openTime: '', closeTime: '', amenities: {}, polygons: [] };
    case 'library':
      return { ...base, groupStudyRooms: [] };
    case 'restaurant':
      return { ...base, menu: '' };
    case 'bathroom':
      return { ...base, bathroomType: '', accessible: false, periodSupply: false };
    case 'office':
      return { ...base, logo: '', links: [] };
    case 'club':
      return { ...base, logo: '', website: '', hoursLinks: [], socialLinks: [], posts: [], instagram: '' };
    default:
      return base;
  }
}

/** The user-facing "not available" line for a blank field. */
export function notAvailable(label) {
  return `${label} is not available right now`;
}

const asArray = (d) => (Array.isArray(d) ? d : Array.isArray(d?.places) ? d.places : []);

/** Live-load the places array (KV → bundled seed, both via the API). */
export async function loadPlaces() {
  try {
    const res = await fetch(`${API_BASE}/api/welcome-day?part=places`);
    if (res.ok) {
      const d = await res.json();
      if (Array.isArray(d) || d?.places) return asArray(d);
    }
  } catch { /* API unreachable */ }
  return [];
}

/** Persist the places array (requires the editor token). @returns {Promise<{ok,message?}>} */
export async function savePlaces(places) {
  try {
    const res = await fetch(`${API_BASE}/api/welcome-day`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify({ op: 'save-places', data: places }),
    });
    if (res.ok) return { ok: true };
    if (res.status === 401) return { ok: false, message: 'Not signed in.' };
    return { ok: false, message: `Save failed (${res.status}).` };
  } catch (e) {
    return { ok: false, message: 'Save failed: ' + (e?.message || e) };
  }
}
